import { Bell, BellOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { PushDeviceToggle } from '@/components/PushDeviceToggle';
import {
  unreadCountOf,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  type NotificationFilter,
  type NotificationsBasePath,
} from '@/hooks/notifications';
import { formatInstant } from '@/lib/dates';
import { cn } from '@/lib/utils';
import type { NotificationView } from '@shared/schemas/notification';

/*
 * The bell and its tray — ROADMAP.md §11.1, one control in all three portals.
 *
 * The query behind it lives in the topbar, which never remounts on navigation,
 * so moving between screens costs zero notification requests. That plus the
 * cache in query-client.ts is the closure of P7 — the prototype fetched this
 * list three to four times per page load and again on every navigation.
 *
 * The badge caps at 9+. A number a person can act on is "there are a few";
 * "47" is a number that has already become an inbox, and the honest render of
 * it is "many" rather than a wider and wider red pill.
 */

export function NotificationTray({ basePath }: { basePath: NotificationsBasePath }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const panelRef = useRef<HTMLDivElement>(null);

  const list = useNotifications(basePath, filter);
  const markRead = useMarkNotificationRead(basePath);
  const markAll = useMarkAllNotificationsRead(basePath);

  const unread = unreadCountOf(list.data);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent): void {
      if (!panelRef.current?.contains(event.target as Node)) setOpen(false);
    }
    // Escape closes it — same keyboard path as the account menu next door.
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function onItemClick(item: NotificationView): void {
    if (!item.isRead) markRead.mutate(item.id);
    // Only in-app paths. linkUrl is written by our own services, but a tray
    // that would follow anything is one refactor away from following an
    // attacker's URL out of the app.
    if (item.linkUrl?.startsWith('/')) {
      setOpen(false);
      void navigate(item.linkUrl);
    }
  }

  const items = list.data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <div ref={panelRef} className="relative">
      <Button
        variant="ghost"
        size="icon"
        aria-label={
          unread === null
            ? 'Notifications'
            : unread === 0
              ? 'Notifications — none unread'
              : `Notifications — ${String(unread)} unread`
        }
        aria-haspopup="dialog"
        aria-expanded={open}
        className="relative"
        onClick={() => {
          setOpen((wasOpen) => !wasOpen);
        }}
      >
        <Bell aria-hidden="true" />
        {unread !== null && unread > 0 ? (
          /*
           * ink-900 on honey-400, the checked pair — a red badge reads as an
           * alarm, and "a client asked for a clean" is work, not fire.
           */
          <span
            aria-hidden="true"
            className="absolute right-0.5 top-0.5 grid min-w-[18px] place-items-center rounded-full bg-honey-400 px-1 text-[11px] font-semibold leading-[18px] text-ink-900"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </Button>

      {open ? (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 top-full z-30 mt-1 flex max-h-[70vh] w-[min(24rem,calc(100vw-1rem))] flex-col rounded-md border border-ink-300 bg-white shadow-pop"
        >
          <div className="flex items-center justify-between gap-2 border-b border-ink-300 px-3 py-2">
            <h2 className="text-h3 text-ink-900">Notifications</h2>
            <Button
              variant="ghost"
              size="sm"
              disabled={markAll.isPending || unread === 0}
              onClick={() => {
                markAll.mutate();
              }}
            >
              Mark all read
            </Button>
          </div>

          <div className="border-b border-ink-300 px-3 py-2">
            <FilterPills
              label="Filter notifications"
              options={[
                { value: 'all', label: 'All' },
                { value: 'unread', label: 'Unread', count: unread ?? undefined },
              ]}
              value={filter}
              onChange={setFilter}
            />
          </div>

          <div className="overflow-y-auto">
            {list.isError ? (
              <ErrorState
                title="Notifications could not be loaded"
                onRetry={() => void list.refetch()}
              />
            ) : list.isPending ? (
              // A skeleton, not a blank — loading is its own state.
              <div aria-hidden="true" className="space-y-2 p-3">
                <div className="h-14 animate-pulse rounded-md bg-ink-100" />
                <div className="h-14 animate-pulse rounded-md bg-ink-100" />
                <div className="h-14 animate-pulse rounded-md bg-ink-100" />
              </div>
            ) : items.length === 0 ? (
              <EmptyState
                icon={BellOff}
                title={filter === 'unread' ? 'Nothing unread' : 'Nothing yet'}
                hint={
                  filter === 'unread'
                    ? 'Everything here has been seen.'
                    : 'When something needs your attention, it lands here.'
                }
              />
            ) : (
              <ul className="divide-y divide-ink-100">
                {items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => {
                        onItemClick(item);
                      }}
                      className={cn(
                        'block min-h-touch w-full px-3 py-2.5 text-left hover:bg-honey-50',
                        'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2',
                        !item.isRead && 'bg-honey-50/60',
                      )}
                    >
                      <span className="flex items-baseline gap-2">
                        {!item.isRead ? (
                          <span
                            aria-hidden="true"
                            className="size-2 shrink-0 self-center rounded-full bg-bark-700"
                          />
                        ) : null}
                        <span
                          className={cn(
                            'min-w-0 flex-1 truncate text-label text-ink-900',
                            !item.isRead && 'font-semibold',
                          )}
                        >
                          {item.title}
                        </span>
                        {!item.isRead ? <span className="sr-only">(unread)</span> : null}
                      </span>
                      <span className="mt-0.5 line-clamp-2 block text-body text-ink-700">
                        {item.body}
                      </span>
                      <span className="mt-0.5 block text-meta text-ink-500">
                        {formatInstant(item.createdAt)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {list.hasNextPage ? (
            <div className="border-t border-ink-300 p-2">
              <Button
                variant="ghost"
                size="sm"
                full
                disabled={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
              >
                {list.isFetchingNextPage ? 'Loading…' : 'Show earlier'}
              </Button>
            </div>
          ) : null}

          <div className="border-t border-ink-300">
            <PushDeviceToggle basePath={basePath} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
